import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock3,
  MapPin,
  Menu as MenuIcon,
  Package,
  Phone,
  ShieldCheck,
  Target,
  Truck,
  Users,
  Warehouse,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import InstallPWAButton from '@/components/InstallPWAButton';

function scrollToHomeSection(navigate: ReturnType<typeof useNavigate>, id: string) {
  navigate(`/#${id}`);
}

export default function AboutPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const goHomeSection = (id: string) => {
    setMenuOpen(false);
    scrollToHomeSection(navigate, id);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" dir="rtl">
      {/* Public Site Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-slate-50/95 backdrop-blur">
        <div className="mx-auto flex min-h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 text-right"
            onClick={() => {
              setMenuOpen(false);
              navigate('/');
            }}
            aria-label="العودة إلى الصفحة الرئيسية"
          >
            <img
              src="/logo-light.png"
              alt="Target Express"
              className="h-14 w-auto object-contain sm:h-16"
            />

            <span className="hidden min-w-0 sm:block">
              <strong className="block truncate text-lg leading-tight">
                Target Express
              </strong>
              <small className="block truncate text-xs text-slate-500">
                Shipping and Logistics Solutions
              </small>
            </span>
          </button>

          <nav
            className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex"
            aria-label="التنقل الرئيسي"
          >
            <button
              type="button"
              onClick={() => goHomeSection('features')}
              className="hover:text-orange-600"
            >
              المميزات
            </button>

            <button
              type="button"
              onClick={() => goHomeSection('how-it-works')}
              className="hover:text-orange-600"
            >
              كيف نعمل
            </button>

            <button
              type="button"
              onClick={() => goHomeSection('pricing')}
              className="hover:text-orange-600"
            >
              الأسعار
            </button>

            <button
              type="button"
              onClick={() => go('/faq')}
              className="hover:text-orange-600"
            >
              الأسئلة الشائعة
            </button>
          </nav>

          <div className="hidden items-center gap-2 sm:flex">
            <Button variant="ghost" onClick={() => go('/login')}>
              تسجيل الدخول
            </Button>

            <Button
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => go('/register')}
            >
              ابدأ كتاجر
            </Button>
          </div>

          <button
            type="button"
            className="rounded-lg p-2 text-slate-700 sm:hidden"
            onClick={() => setMenuOpen(value => !value)}
            aria-label={menuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <MenuIcon className="h-6 w-6" />
            )}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 sm:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-2 text-right">
              <button
                type="button"
                className="rounded-lg px-3 py-3 text-right hover:bg-slate-50"
                onClick={() => goHomeSection('features')}
              >
                المميزات
              </button>

              <button
                type="button"
                className="rounded-lg px-3 py-3 text-right hover:bg-slate-50"
                onClick={() => goHomeSection('how-it-works')}
              >
                كيف نعمل
              </button>

              <button
                type="button"
                className="rounded-lg px-3 py-3 text-right hover:bg-slate-50"
                onClick={() => goHomeSection('pricing')}
              >
                الأسعار
              </button>

              <button
                type="button"
                className="rounded-lg px-3 py-3 text-right hover:bg-slate-50"
                onClick={() => go('/faq')}
              >
                الأسئلة الشائعة
              </button>

              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
                <Button variant="outline" onClick={() => go('/login')}>
                  تسجيل الدخول
                </Button>

                <Button
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={() => go('/register')}
                >
                  ابدأ كتاجر
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.24),transparent_42%),radial-gradient(circle_at_bottom_left,rgba(37,99,235,0.24),transparent_42%)]" />

          <div className="relative mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 lg:py-28">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center">
              <img
                src="/icon-512-from-server.png"
                alt="Target Express"
                className="h-16 w-16 object-contain"
              />
            </div>

            <p className="mb-3 font-semibold text-orange-400">من نحن</p>

            <h1 className="mb-3 text-4xl font-black leading-tight sm:text-5xl">
              Target Express
            </h1>

            <p className="mb-6 text-xl font-medium text-slate-300 sm:text-2xl">
              We Deliver Your Goals
            </p>

            <p className="mx-auto max-w-3xl text-base leading-8 text-slate-300 sm:text-lg sm:leading-9">
              Target Express هي شركة متخصصة في الشحن والخدمات اللوجستية داخل
              مصر، نعمل على تقديم حلول متكاملة تساعد الشركات والمتاجر
              الإلكترونية على إدارة عملياتها اللوجستية بكفاءة وسهولة.
            </p>
          </div>
        </section>

        {/* About */}
        <section className="bg-white py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <div className="mx-auto max-w-4xl text-center">
              <span className="mb-5 inline-block rounded-full bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-700">
                منظومة لوجستية متكاملة
              </span>

              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">
                أكثر من مجرد خدمة شحن
              </h2>

              <p className="mb-5 text-lg leading-9 text-slate-600">
                من خلال منظومة تشمل التوصيل، التحصيل، التخزين، وتجهيز الطلبات،
                نربط بين أعمالك وعملائك عبر تجربة لوجستية أكثر تنظيمًا ومرونة.
              </p>

              <p className="text-lg leading-9 text-slate-600">
                نحن لا ننظر إلى الشحن باعتباره مجرد نقل منتج من مكان إلى آخر،
                بل نراه جزءًا أساسيًا من تجربة العميل ونجاح أي نشاط تجاري.
                لذلك نعمل على بناء منظومة تساعدك على إدارة التفاصيل اليومية،
                وتمنحك القدرة على التركيز على تطوير أعمالك.
              </p>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="bg-slate-50 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mb-12 text-center">
              <p className="mb-2 font-semibold text-orange-600">خدماتنا</p>

              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                كل ما تحتاجه في منظومة واحدة
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-sm transition-shadow hover:shadow-lg">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
                  <Truck className="h-7 w-7 text-orange-600" />
                </div>

                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  التوصيل
                </h3>

                <p className="text-sm leading-7 text-slate-600">
                  ربط أعمالك بعملائك من خلال حلول توصيل تساعدك على إدارة
                  عملياتك بشكل أكثر سهولة وتنظيمًا.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-sm transition-shadow hover:shadow-lg">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100">
                  <Warehouse className="h-7 w-7 text-blue-600" />
                </div>

                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  التخزين
                </h3>

                <p className="text-sm leading-7 text-slate-600">
                  حلول تخزين تساعدك على تنظيم مخزونك وإدارة عملياتك اللوجستية
                  بكفاءة ومرونة.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-sm transition-shadow hover:shadow-lg">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100">
                  <Package className="h-7 w-7 text-emerald-600" />
                </div>

                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  تجهيز الطلبات
                </h3>

                <p className="text-sm leading-7 text-slate-600">
                  تجهيز الطلبات ضمن منظومة متكاملة تساعدك على تقليل التفاصيل
                  اليومية والتركيز على نمو أعمالك.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-sm transition-shadow hover:shadow-lg">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100">
                  <WalletIcon />
                </div>

                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  التحصيل
                </h3>

                <p className="text-sm leading-7 text-slate-600">
                  إدارة عمليات التحصيل ضمن منظومة لوجستية واحدة أكثر تنظيمًا
                  ومرونة.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="bg-white py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="rounded-3xl bg-slate-950 p-8 text-white md:p-10">
                <div className="mb-6 flex h-14 w-14 items-center justify-center">
                  <img
                    src="/icon-512-from-server.png"
                    alt="Target Express"
                    className="h-12 w-12 object-contain"
                  />
                </div>

                <h2 className="mb-4 text-2xl font-bold">رؤيتنا</h2>

                <p className="leading-8 text-slate-300">
                  أن نكون شريكًا لوجستيًا موثوقًا للشركات والمتاجر الإلكترونية
                  في مصر، وأن نساهم في تطوير تجربة الشحن واللوجستيات من خلال
                  حلول عملية ومرنة تواكب نمو الأعمال.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 md:p-10">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
                  <ShieldCheck className="h-7 w-7 text-orange-600" />
                </div>

                <h2 className="mb-4 text-2xl font-bold text-slate-900">
                  رسالتنا
                </h2>

                <p className="leading-8 text-slate-600">
                  تقديم خدمات لوجستية متكاملة تجمع بين الكفاءة والمرونة،
                  وتساعد عملاءنا على تبسيط عملياتهم والوصول إلى عملائهم بشكل
                  أكثر سهولة وتنظيمًا.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="bg-slate-50 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mb-12 text-center">
              <p className="mb-2 font-semibold text-orange-600">
                ما الذي يميزنا؟
              </p>

              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">
                شريك لوجستي لنمو أعمالك
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Feature
                icon={<Truck className="h-6 w-6 text-orange-600" />}
                title="منظومة متكاملة"
                text="نجمع خدمات التخزين والتجهيز والتوصيل والتحصيل ضمن منظومة واحدة."
              />

              <Feature
                icon={<MapPin className="h-6 w-6 text-blue-600" />}
                title="تغطية واسعة"
                text="نقدم خدماتنا في مختلف أنحاء جمهورية مصر العربية."
              />

              <Feature
                icon={<ShieldCheck className="h-6 w-6 text-emerald-600" />}
                title="مرونة في الحلول"
                text="نصمم خدماتنا بما يتناسب مع طبيعة كل نشاط وحجم عملياته."
              />

              <Feature
                icon={<Users className="h-6 w-6 text-purple-600" />}
                title="شريك لنمو أعمالك"
                text="نعمل كامتداد لفريقك، ونتولى الجانب اللوجستي حتى تتمكن من التركيز على تطوير أعمالك."
              />
            </div>
          </div>
        </section>

        {/* Goal */}
        <section className="bg-white py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-sm">
              <img
                src="/logo-light.png"
                alt="Target Express"
                className="h-16 w-auto object-contain"
              />
            </div>

            <p className="mb-3 font-semibold text-orange-600">هدفنا</p>

            <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">
              اللوجستيات أسهل. أعمالك أقوى.
            </h2>

            <p className="mb-5 text-lg leading-9 text-slate-600">
              هدفنا بسيط: أن نجعل اللوجستيات أسهل على عملائنا، وأن يكون وصول
              منتجاتهم إلى عملائهم أكثر سلاسة وتنظيمًا.
            </p>

            <p className="text-xl font-bold text-slate-900">
              لأن نجاحك هو الهدف الذي نعمل من أجله.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-orange-600 py-16">
          <div className="mx-auto max-w-3xl px-4 text-center text-white sm:px-6">
            <h2 className="mb-4 text-3xl font-bold">Target Express</h2>

            <p className="mb-7 text-xl text-orange-100">
              We Deliver Your Goals
            </p>

            <Button
              size="lg"
              className="bg-white font-bold text-orange-700 hover:bg-slate-100"
              onClick={() => go('/register')}
            >
              ابدأ كتاجر
            </Button>
          </div>
        </section>
      </main>

      {/* Public Site Footer */}
      <footer className="bg-slate-950 py-12 text-slate-300">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-4">
          <div>
            <img
              src="/icon-512-from-server.png"
              alt="Target Express"
              className="h-16 w-auto object-contain"
            />

            <p className="mt-4 text-sm leading-7 text-slate-400">
              حلول شحن وتنفيذ تساعد المتاجر على إدارة الطلبات والتوصيل
              والتحصيل بوضوح.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-white">روابط سريعة</h3>

            <div className="mt-4 space-y-3 text-sm">
              <button
                type="button"
                onClick={() => go('/track')}
                className="block hover:text-orange-400"
              >
                تتبع شحنتك
              </button>

              <button
                type="button"
                onClick={() => go('/about')}
                className="block hover:text-orange-400"
              >
                من نحن
              </button>

              <button
                type="button"
                onClick={() => go('/contact')}
                className="block hover:text-orange-400"
              >
                اتصل بنا
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-white">خدماتنا</h3>

            <div className="mt-4 space-y-3 text-sm text-slate-400">
              <p>شحن داخلي</p>
              <p>تحصيل عند الاستلام</p>
              <p>تتبع الشحنات</p>
              <p>تخزين وتجهيز</p>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-white">تواصل معنا</h3>

            <div className="mt-4 space-y-3 text-sm text-slate-400">
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-orange-500" />
                01008524717
              </p>

              <p className="flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-500" />
                info@targetexpress.com
              </p>

              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-orange-500" />
                القاهرة، مصر
              </p>

              <p className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-orange-500" />
                دعم خلال ساعات العمل
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-7xl border-t border-white/10 px-4 pt-6 text-center text-xs text-slate-500 sm:px-6">
          © 2026 Target Express. جميع الحقوق محفوظة.
        </div>
      </footer>

      <InstallPWAButton />
    </div>
  );
}

function Feature({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-50">
          {icon}
        </div>

        <div>
          <h3 className="mb-2 text-xl font-bold text-slate-900">{title}</h3>
          <p className="leading-7 text-slate-600">{text}</p>
        </div>
      </div>
    </div>
  );
}

function WalletIcon() {
  return (
    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-purple-100 text-purple-600">
      <span className="text-sm font-bold">₿</span>
    </div>
  );
}


